"use client";

import * as React from "react";
import { Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { cn } from "~/lib/utils";
import {
  HIRE_ONBOARDING_GENERATE_STEP_INDEX,
  HIRE_ONBOARDING_PROGRESS_STEPS,
  HIRE_ONBOARDING_SIGNUP_STEP_INDEX,
} from "~/lib/marketplace/hire-onboarding-progress-steps";

type HireOnboardingProgressModalProps = {
  open: boolean;
  activeStepIndex: number;
  /** When true, show website URL form before analysis begins. */
  needsWebsiteInput?: boolean;
  errorMessage?: string | null;
  isGeneratingPreview?: boolean;
  /** Signed-out users must authenticate before preview generation. */
  needsSignIn?: boolean;
  /** Allow dismissing (SiteGlue: user can keep chatting with Trish). */
  dismissible?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  onSubmitWebsite?: (url: string) => void;
  onSignIn?: () => void;
};

export function HireOnboardingProgressModal({
  open,
  activeStepIndex,
  needsWebsiteInput = false,
  errorMessage,
  isGeneratingPreview = false,
  needsSignIn = false,
  dismissible = false,
  onOpenChange,
  title,
  description,
  onSubmitWebsite,
  onSignIn,
}: HireOnboardingProgressModalProps) {
  const [websiteDraft, setWebsiteDraft] = React.useState("");

  const handleWebsiteSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = websiteDraft.trim();
    if (!trimmed) return;
    onSubmitWebsite?.(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={dismissible ? onOpenChange : undefined}>
      <DialogContent
        className={cn(
          "max-w-md gap-0 p-0 sm:max-w-lg",
          !dismissible && "[&>button]:hidden",
        )}
        onPointerDownOutside={(e) => {
          if (!dismissible) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (!dismissible) e.preventDefault();
        }}
      >
        <DialogHeader className="border-b px-6 py-5 text-left">
          <DialogTitle className="text-lg font-semibold text-slate-900">
            {title?.trim() || "Onboarding Progress"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            {needsWebsiteInput
              ? "Enter your business website to analyze your company and recommend agent settings."
              : description?.trim() ||
                "Trish is setting up your AI Employee from your website. Here’s what’s happening now."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-5">
          {needsWebsiteInput ? (
            <form onSubmit={handleWebsiteSubmit} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="hire-bootstrap-website">Business website</Label>
                <Input
                  id="hire-bootstrap-website"
                  placeholder="https://yourbusiness.com"
                  value={websiteDraft}
                  onChange={(e) => setWebsiteDraft(e.target.value)}
                  autoComplete="url"
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={!websiteDraft.trim()}>
                Analyze website
              </Button>
            </form>
          ) : null}

          {!needsWebsiteInput ? (
            <ul className="space-y-3" aria-live="polite">
              {HIRE_ONBOARDING_PROGRESS_STEPS.map((label, index) => {
                const isComplete = index < activeStepIndex;
                const isGenerateStep = index === HIRE_ONBOARDING_GENERATE_STEP_INDEX;
                const isSignupStep = index === HIRE_ONBOARDING_SIGNUP_STEP_INDEX;
                const isActive =
                  index === activeStepIndex ||
                  (isGenerateStep && isGeneratingPreview && activeStepIndex >= index);
                const isPending = index > activeStepIndex && !isActive;
                const showSpinner = isActive && isGenerateStep && isGeneratingPreview;

                return (
                  <li
                    key={label}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors",
                      isActive && "border-primary/30 bg-primary/5",
                      isComplete && "border-slate-200 bg-slate-50/80",
                      isPending && "border-transparent opacity-60",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                        isComplete && "bg-emerald-100 text-emerald-700",
                        isActive && "bg-primary/15 text-primary",
                        isPending && "bg-slate-100 text-slate-400",
                      )}
                      aria-hidden
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : showSpinner ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : isActive ? (
                        isSignupStep ? (
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        ) : (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        )
                      ) : (
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      )}
                    </span>
                    <span
                      className={cn(
                        "text-sm",
                        isActive || isComplete ? "font-medium text-slate-900" : "text-slate-600",
                      )}
                    >
                      {label}
                      {isGenerateStep && isActive && isGeneratingPreview ? "…" : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {errorMessage ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </p>
          ) : null}

          {needsSignIn &&
          !needsWebsiteInput &&
          activeStepIndex >= HIRE_ONBOARDING_SIGNUP_STEP_INDEX ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <p className="font-medium text-slate-900">Sign Up | Create SiteGlue Account</p>
              <p className="mt-1 text-slate-600">
                Create your free account to save your AI Employee and continue testing.
              </p>
              {onSignIn ? (
                <Button type="button" size="sm" className="mt-3" onClick={onSignIn}>
                  Sign Up | Create SiteGlue Account
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
